import { useState } from "react";
import {
  CANDIDATE_STATUSES,
  CANDIDATE_STATUS_LABELS,
  EDUCATION_LABELS,
  EDUCATION_LEVELS,
  SEXES,
  SEX_LABELS,
  type Candidate,
  type CandidateUpdate,
} from "@shared/modules/x-candidats";
import { Button } from "../../../core/components/ui/button";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";

export type Draft = {
  firstName: string;
  lastName: string;
  sex: string;
  birthDate: string;
  nationality: string;
  countryResidence: string;
  city: string;
  email: string;
  phone: string;
  profession: string;
  educationLevel: string;
  educationDetail: string;
  experienceYears: string;
  languages: string;
  status: string;
  notes: string;
  passportNumber: string;
  passportExpiresAt: string;
};

export function draftFrom(c?: Candidate | null): Draft {
  return {
    firstName: c?.firstName ?? "",
    lastName: c?.lastName ?? "",
    sex: c?.sex ?? "",
    birthDate: c?.birthDate ?? "",
    nationality: c?.nationality ?? "",
    countryResidence: c?.countryResidence ?? "",
    city: c?.city ?? "",
    email: c?.email ?? "",
    phone: c?.phone ?? "",
    profession: c?.profession ?? "",
    educationLevel: c?.educationLevel ?? "",
    educationDetail: c?.educationDetail ?? "",
    experienceYears: c?.experienceYears != null ? String(c.experienceYears) : "",
    languages: (c?.languages ?? []).join(", "),
    status: c?.status ?? "nouveau",
    notes: c?.notes ?? "",
    passportNumber: c?.passportNumber ?? "",
    passportExpiresAt: c?.passportExpiresAt ?? "",
  };
}

/** Transforme le brouillon en entrée API (chaînes vides → null, listes séparées par des virgules). */
export function toInput(d: Draft): CandidateUpdate {
  const text = (v: string) => (v.trim() ? v.trim() : null);
  return {
    firstName: d.firstName.trim(),
    lastName: d.lastName.trim(),
    sex: (d.sex || null) as CandidateUpdate["sex"],
    birthDate: d.birthDate || null,
    nationality: text(d.nationality),
    countryResidence: text(d.countryResidence),
    city: text(d.city),
    email: d.email.trim() || null,
    phone: text(d.phone),
    profession: text(d.profession),
    educationLevel: (d.educationLevel || null) as CandidateUpdate["educationLevel"],
    educationDetail: text(d.educationDetail),
    experienceYears: d.experienceYears.trim() ? Number(d.experienceYears) : null,
    languages: d.languages
      .split(",")
      .map((l) => l.trim())
      .filter(Boolean),
    status: d.status as CandidateUpdate["status"],
    notes: d.notes.trim() ? d.notes : null,
    passportNumber: text(d.passportNumber),
    passportExpiresAt: d.passportExpiresAt || null,
  };
}

export function CandidateForm({
  initial,
  submitLabel,
  onSubmit,
  onCancel,
  busy,
}: {
  initial: Draft;
  submitLabel: string;
  onSubmit: (d: Draft) => void;
  onCancel?: () => void;
  busy?: boolean;
}) {
  const [d, setD] = useState<Draft>(initial);
  const set = (k: keyof Draft) => (e: { target: { value: string } }) => setD({ ...d, [k]: e.target.value });

  return (
    <form
      className="candidat-form"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(d);
      }}
    >
      <section className="form-section">
        <div className="form-section__head">
          <h3 className="form-section__title">Identité</h3>
          <p className="form-section__hint">La date de naissance et le numéro de passeport sont chiffrés ; seule l'année de naissance sert aux recherches.</p>
        </div>
        <div className="form-section__grid">
          <Field label="Prénom">
            <Input value={d.firstName} onChange={set("firstName")} required />
          </Field>
          <Field label="Nom">
            <Input value={d.lastName} onChange={set("lastName")} required />
          </Field>
          <Field label="Sexe">
            <Select value={d.sex} onChange={set("sex")}>
              <option value="">—</option>
              {SEXES.map((s) => (
                <option key={s} value={s}>
                  {SEX_LABELS[s]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Date de naissance">
            <Input type="date" value={d.birthDate} onChange={set("birthDate")} />
          </Field>
          <Field label="Nationalité">
            <Input value={d.nationality} onChange={set("nationality")} />
          </Field>
          <Field label="Pays de résidence">
            <Input value={d.countryResidence} onChange={set("countryResidence")} />
          </Field>
          <Field label="Ville">
            <Input value={d.city} onChange={set("city")} />
          </Field>
          <Field label="Courriel">
            <Input type="email" value={d.email} onChange={set("email")} />
          </Field>
          <Field label="Téléphone">
            <Input value={d.phone} onChange={set("phone")} />
          </Field>
          <Field label="Numéro de passeport">
            <Input value={d.passportNumber} onChange={set("passportNumber")} autoComplete="off" />
          </Field>
          <Field label="Expiration du passeport">
            <Input type="date" value={d.passportExpiresAt} onChange={set("passportExpiresAt")} />
          </Field>
        </div>
      </section>

      <section className="form-section">
        <div className="form-section__head">
          <h3 className="form-section__title">Profil professionnel</h3>
        </div>
        <div className="form-section__grid">
          <Field label="Profession">
            <Input value={d.profession} onChange={set("profession")} />
          </Field>
          <Field label="Niveau d'études">
            <Select value={d.educationLevel} onChange={set("educationLevel")}>
              <option value="">—</option>
              {EDUCATION_LEVELS.map((l) => (
                <option key={l} value={l}>
                  {EDUCATION_LABELS[l]}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Années d'expérience">
            <Input type="number" min={0} max={60} value={d.experienceYears} onChange={set("experienceYears")} />
          </Field>
          <Field label="Langues" hint="Séparées par des virgules.">
            <Input value={d.languages} onChange={set("languages")} />
          </Field>
          <Field label="Statut du dossier">
            <Select value={d.status} onChange={set("status")}>
              {CANDIDATE_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {CANDIDATE_STATUS_LABELS[s]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <div className="form-section__grid form-section__grid--full">
          <Field label="Études, diplômes (détail)">
            <Textarea rows={2} value={d.educationDetail} onChange={set("educationDetail")} />
          </Field>
          <Field label="Notes">
            <Textarea rows={4} value={d.notes} onChange={set("notes")} />
          </Field>
        </div>
      </section>

      <div className="candidat-form__actions">
        {onCancel ? (
          <Button type="button" variant="ghost" onClick={onCancel}>
            Annuler
          </Button>
        ) : null}
        <Button type="submit" variant="primary" disabled={busy}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}
